/**
 * useRelationshipEntries Hook
 *
 * React Query hooks for relationship tracker entry CRUD operations.
 * Provides data fetching, mutations, and stats queries with cache management.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createEntry,
  deleteEntry,
  getStats,
  listEntries,
} from "../services/relationshipEntries";
import type {
  RelationshipEntry,
  RelationshipEntryCreate,
  RelationshipEntryListResponse,
  RelationshipEntryStats,
} from "../types/relationshipEntry";

/**
 * Fetch entries for a date range (used for calendar display)
 */
export function useRelationshipEntries(startDate: string, endDate: string) {
  return useQuery<RelationshipEntryListResponse, Error>({
    queryKey: ["relationshipEntries", startDate, endDate],
    queryFn: () => listEntries(startDate, endDate),
    enabled: !!startDate && !!endDate,
  });
}

/**
 * Get aggregate stats for a date range
 */
export function useRelationshipEntryStats(startDate: string, endDate: string) {
  return useQuery<RelationshipEntryStats, Error>({
    queryKey: ["relationshipEntryStats", startDate, endDate],
    queryFn: () => getStats(startDate, endDate),
    enabled: !!startDate && !!endDate,
  });
}

/**
 * Create or update an entry (upsert)
 */
export function useCreateRelationshipEntry() {
  const queryClient = useQueryClient();

  return useMutation<RelationshipEntry, Error, RelationshipEntryCreate>({
    mutationFn: (data: RelationshipEntryCreate) => createEntry(data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["relationshipEntries"],
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: ["relationshipEntryStats"],
          refetchType: "all",
        }),
      ]);
    },
  });
}

/**
 * Delete an entry
 */
export function useDeleteRelationshipEntry() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteEntry(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["relationshipEntries"],
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: ["relationshipEntryStats"],
          refetchType: "all",
        }),
      ]);
    },
  });
}
